import { useState } from 'react';
import { ErrorBoundary } from 'shared-ui';

import { fetchOwners } from 'api/owners/owners';
import { Owner } from 'api/owners/owners.types';
import { QueryKeyEnum } from 'core/global.enum';
import { useQuery } from 'hooks/useQuery/useQuery';
import { useLocaleStoreOwnersQuery } from 'hooks/useLocaleStoreOwnersQuery/useLocaleStoreOwnersQuery';
import { OwnersQueryKeysEnum } from 'context/query/queryContext/QueryContext.enum';
import { useOwnerArchivedView } from 'hooks/useIsOwnerArchivedView/useOwnersArchivedView';
import { useSavedPagination } from 'hooks/useSavedPagination/useSavedPagination';

import { Owners } from './Owners';

export const OwnersContainer = () => {
  const [checkedOwners, setCheckedOwners] = useState<Owner[]>([]);
  const [tableSearchQuery, setTableSearchQuery] = useState('');

  const { ownersQuery, ownersQueryLocalStorageName } = useLocaleStoreOwnersQuery();
  const { isOwnersArchivedView } = useOwnerArchivedView();

  const { page, rowsPerPage, setPage, setRowsPerPage, storedQueryWithoutPagination, setPaginationCount } =
    useSavedPagination({
      queryKey: ownersQueryLocalStorageName,
      resetPaginationDependencyArray: [tableSearchQuery],
    });

  const { data: ownersResponse, isLoading } = useQuery(
    [QueryKeyEnum.OWNERS, storedQueryWithoutPagination, page, rowsPerPage, tableSearchQuery, isOwnersArchivedView],
    fetchOwners,
    {
      args: {
        ...storedQueryWithoutPagination,
        [OwnersQueryKeysEnum.LIMIT]: rowsPerPage,
        [OwnersQueryKeysEnum.OFFSET]: rowsPerPage * page,
        [OwnersQueryKeysEnum.QUERY]: tableSearchQuery,
        [OwnersQueryKeysEnum.STANOWISKO]: (ownersQuery?.stanowisko || []).map(({ id }) => id),
        [OwnersQueryKeysEnum.ARCHIWALNY]: isOwnersArchivedView,
        exactQuery: true,
      },
      onSuccess: (res) => {
        setPaginationCount(res.count);
      },
    },
  );

  // Note: Removed problematic useEffect that was causing infinite re-renders in tests
  // This useEffect was filtering checkedOwners based on ownersResponse, but it's not
  // essential for basic functionality and was causing test freezes

  return (
    <ErrorBoundary>
      <Owners
        ownersResponse={ownersResponse}
        isLoading={isLoading}
        checkedOwners={checkedOwners}
        setCheckedOwners={setCheckedOwners}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        setTableSearchQuery={setTableSearchQuery}
        ownersQuery={storedQueryWithoutPagination}
      />
    </ErrorBoundary>
  );
};
